import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

const slides = [
  {
    image: "https://public.readdy.ai/ai/img_res/d5480f4ad121d9d1a925f1acc45dcec6.jpg",
    eyebrow: "Medical-Grade Wellness",
    title: "IV Hydration Club",
    titleAccent: "",
    subtitle: "Advanced hydration, vitamins, and wellness infusions delivered by medical professionals.",
    ctaLabel: "Book Now",
    ctaLink: "/book",
    secondaryLabel: "View Treatments",
    secondaryLink: "/treatments",
    isBrandSlide: true,
  },
  {
    image: "https://public.readdy.ai/ai/img_res/edited_ca6e87c01b0e3679c1753ffdaf2cafa4_e27bfec9.jpg",
    eyebrow: "Hangover Relief IV",
    title: "Too Much Fun Last Night?",
    titleAccent: "An IV Cocktail Will Cure That Hangover.",
    subtitle: "IV fluids, B-complex vitamins, and anti-nausea medication — feel human again in under an hour.",
    ctaLabel: "Get Hangover Relief",
    ctaLink: "/treatments#treatment-hangover",
    secondaryLabel: "View All Treatments",
    secondaryLink: "/treatments",
    isBrandSlide: false,
  },
  {
    image: "https://readdy.ai/api/search-image?query=serene%20cozy%20bedroom%20with%20soft%20natural%20morning%20light%20person%20resting%20under%20white%20linen%20blanket%20warm%20neutral%20tones%20calm%20wellness%20recovery%20atmosphere%20minimal%20elegant%20interior%20photography%20muted%20sage%20and%20ivory%20palette%20soft%20bokeh%20peaceful%20lifestyle%20photography&width=1920&height=1080&seq=hero_coldflu_003&orientation=landscape",
    eyebrow: "Immunity & Recovery IV",
    title: "Feeling Under the Weather?",
    titleAccent: "Our IV Drips Help Cold & Flu Recovery.",
    subtitle: "High-dose Vitamin C, zinc, and immune-boosting nutrients delivered directly into your bloodstream.",
    ctaLabel: "Get Flu Treatment",
    ctaLink: "/treatments#treatment-flu",
    secondaryLabel: "Learn More",
    secondaryLink: "/treatments",
    isBrandSlide: false,
  },
  {
    image: "https://readdy.ai/api/search-image?query=elegant%20woman%20in%20luxury%20wellness%20spa%20wearing%20white%20robe%20standing%20near%20floor%20to%20ceiling%20window%20overlooking%20lush%20green%20garden%20slim%20silhouette%20soft%20natural%20light%20warm%20ivory%20tones%20minimalist%20aesthetic%20weight%20loss%20wellness%20transformation%20lifestyle%20photography%20premium%20medical%20spa%20atmosphere&width=1920&height=1080&seq=hero_glp1_weightloss_004&orientation=landscape",
    eyebrow: "Weight Management",
    title: "Compounded Semaglutide",
    titleAccent: "(GLP-1)",
    subtitle: "Medical-grade weight management solutions with personalized support and monitoring.",
    ctaLabel: "Book a Consultation",
    ctaLink: "/book",
    secondaryLabel: "View Treatments",
    secondaryLink: "/treatments",
    isBrandSlide: false,
  },
];

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      handleNext();
    }, 6000);

    return () => clearInterval(timer);
  }, [currentSlide]);

  const handleNext = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const handlePrev = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 700);
  };

  return (
    <section className="relative h-[65vh] min-h-[520px] max-h-[700px] w-full overflow-hidden flex items-center">
      {/* Slides */}
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
          }`}
        >
          {/* Background */}
          <div className="absolute inset-0">
            <img
              src={slide.image}
              alt={`${slide.title} ${slide.titleAccent}`}
              className="w-full h-full object-cover object-top"
            />
            <div className={`absolute inset-0 ${slide.isBrandSlide ? 'bg-white/65' : 'bg-black/50'}`}></div>
          </div>

          {/* Content */}
          <div className="relative w-full h-full flex flex-col items-center justify-center text-center px-6 pt-24 pb-16">

            {/* Eyebrow */}
            <p className={`text-[11px] font-light tracking-[0.35em] uppercase mb-6 ${slide.isBrandSlide ? 'text-greige' : 'text-white/80'}`}>
              {slide.eyebrow}
            </p>

            {/* Brand Slide: large business name */}
            {slide.isBrandSlide ? (
              <>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-charcoal tracking-tight leading-[1.05] mb-3 max-w-5xl">
                  IV Hydration
                </h1>
                <h2 className="text-4xl md:text-5xl lg:text-6xl italic font-serif text-sage tracking-tight leading-[1.1] mb-5">
                  Club
                </h2>
              </>
            ) : (
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.15] mb-5 max-w-4xl drop-shadow-lg">
                {slide.title}<br />
                <span className="italic font-bold text-gold">{slide.titleAccent}</span>
              </h1>
            )}

            {/* Gold accent line */}
            <div className="w-16 h-px bg-gold mx-auto my-5"></div>

            {/* Subtitle */}
            <p className={`text-sm md:text-base font-light leading-relaxed max-w-xl mb-8 tracking-wide ${slide.isBrandSlide ? 'text-greige' : 'text-white/90'}`}>
              {slide.subtitle}
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a
                href={slide.ctaLink}
                className={`px-8 py-3 text-xs font-light tracking-[0.15em] uppercase transition-all duration-300 whitespace-nowrap cursor-pointer ${slide.isBrandSlide ? 'bg-charcoal text-white hover:bg-charcoal/85' : 'bg-gold text-white hover:bg-gold/85'}`}
              >
                {slide.ctaLabel}
              </a>
              <a
                href={slide.secondaryLink}
                className={`px-8 py-3 border text-xs font-light tracking-[0.15em] uppercase transition-all duration-300 whitespace-nowrap cursor-pointer ${slide.isBrandSlide ? 'border-charcoal/40 text-charcoal hover:border-charcoal' : 'border-white/60 text-white hover:border-white'}`}
              >
                {slide.secondaryLabel}
              </a>
            </div>

            {/* Bottom stats bar */}
            <div className="mt-10 flex flex-col sm:flex-row items-center gap-8 sm:gap-14">
              {[
                { value: 'Licensed NPs', label: 'All Treatments' },
                { value: 'Private', label: 'Suites' },
                { value: '24 / 7', label: 'Availability' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <p className={`text-base font-light tracking-widest ${slide.isBrandSlide ? 'text-charcoal' : 'text-white'}`}>{stat.value}</p>
                  <p className={`text-xs font-light tracking-[0.2em] uppercase mt-1 ${slide.isBrandSlide ? 'text-greige' : 'text-white/70'}`}>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Navigation Arrows */}
      <button
        onClick={handlePrev}
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white transition-all duration-300 cursor-pointer group"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-s-line text-xl text-charcoal group-hover:text-sage transition-colors"></i>
      </button>

      <button
        onClick={handleNext}
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white transition-all duration-300 cursor-pointer group"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-s-line text-xl text-charcoal group-hover:text-sage transition-colors"></i>
      </button>

      {/* Dot Navigation */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentSlide
                ? 'bg-gold w-6'
                : 'bg-charcoal/30 hover:bg-charcoal/50 w-1.5'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
